import styled from "@emotion/styled";
import { motion, useReducedMotion } from "framer-motion";
import { type FC, useRef } from "react";
import {
  DismissButton,
  FocusScope,
  useDialog,
  useOverlay,
  usePreventScroll
} from "react-aria";
import Button from "../../atom/button/Button";

export interface AnnouncementPopupProps {
  title: string;
  body: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  image?: string;
  imageAlt?: string;
}

//#region Styled Components
const Overlay = styled(motion.div)`
  label: AnnouncementPopupOverlay;
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  z-index: 999999;
`;

const Card = styled(motion.div)`
  label: AnnouncementPopupCard;
  background-color: #fff;
  border-radius: 1.5rem;
  overflow: hidden;
  max-width: 345px;
  width: 100%;
  position: relative;
`;

const GradientHeader = styled.div`
  label: AnnouncementPopupGradientHeader;
  background: var(
    --gradient-sunrise-light,
    linear-gradient(135deg, #a6c6ff 13.3%, #fad0e8 48.44%, #ffd199 81.45%)
  );
  height: 166px;
  position: relative;
`;

const HeaderImage = styled.img`
  label: AnnouncementPopupHeaderImage;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
`;

const CurveTransition = styled.svg`
  label: AnnouncementPopupCurve;
  display: block;
  transform: translateY(-100%);
  position: absolute;
`;

const ContentSection = styled.div`
  label: AnnouncementPopupContent;
  background: #fff;
  padding: 1rem 1.5rem 1rem;
  text-align: center;
`;

const Title = styled.h2`
  color: var(--Greys-Charcoal, #212121);
  text-align: center;
  font-family: var(--font-family-serif, "STK Bureau Serif");
  font-size: 28px;
  font-style: normal;
  font-weight: 300;
  line-height: 120%;
  letter-spacing: -0.56px;
  margin: 0 0 0.75rem;
`;

const Body = styled.p`
  color: var(--Greys-Charcoal80, rgba(33, 33, 33, 0.8));
  text-align: center;
  font-family: var(--font-family, "STK Bureau");
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  letter-spacing: -0.32px;
  margin: 0 0 1.6rem;
`;

const CancelButton = styled.button`
  label: AnnouncementPopupCancel;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-charcoal, #212121);
  padding: 0.875rem 1rem;
  margin-top: 0.5rem;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:focus-visible {
    outline: 2px solid var(--Accessible-Blue, #3973da);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;
//#endregion

const cardVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring" as const,
      damping: 22,
      stiffness: 340,
      mass: 0.8
    }
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: { duration: 0.18, ease: [0.4, 0, 1, 1] as const }
  }
};

const cardVariantsReduced = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.15 } }
};

const AnnouncementPopup: FC<AnnouncementPopupProps> = ({
  title,
  body,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  image,
  imageAlt = ""
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  usePreventScroll();

  const { overlayProps } = useOverlay(
    { isOpen: true, onClose: onCancel, isDismissable: true },
    ref
  );
  const { dialogProps, titleProps } = useDialog({}, ref);

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: prefersReducedMotion ? 0 : 0.3,
        ease: [0.25, 0.1, 0.25, 1]
      }}
    >
      <FocusScope contain restoreFocus autoFocus>
        <Card
          ref={ref}
          variants={prefersReducedMotion ? cardVariantsReduced : cardVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          aria-modal="true"
          {...(overlayProps as Record<string, unknown>)}
          {...(dialogProps as Record<string, unknown>)}
        >
          <DismissButton onDismiss={onCancel} />
          <GradientHeader>
            {image && <HeaderImage src={image} alt={imageAlt} />}
          </GradientHeader>
          <CurveTransition
            viewBox="0 0 345 40"
            preserveAspectRatio="none"
            width="100%"
            height="40"
            aria-hidden="true"
          >
            <path d="M0,40 Q172.5,0 345,40 Z" fill="#fff" />
          </CurveTransition>
          <ContentSection>
            <Title {...titleProps}>{title}</Title>
            <Body>{body}</Body>
            <Button fullWidth onPress={onConfirm}>
              {confirmText}
            </Button>
            <CancelButton type="button" onClick={onCancel}>
              {cancelText}
            </CancelButton>
          </ContentSection>
          <DismissButton onDismiss={onCancel} />
        </Card>
      </FocusScope>
    </Overlay>
  );
};

export default AnnouncementPopup;
